import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Brain,
  Sparkles,
  TrendingUp,
  ShoppingCart,
  Truck,
  MessageCircle,
  Download,
  FileText,
  Loader2,
  ChevronRight,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight,
  Package,
  Euro,
  Calculator,
  Utensils
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIAssistent = () => {
  const [activeTab, setActiveTab] = useState("chat");
  const [loading, setLoading] = useState(false);
  
  // Chat State
  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  
  // Preiskalkulation State
  const [preisForm, setPreisForm] = useState({
    name: "",
    kategorie: "Speise",
    zutaten_kosten: "",
    portionsgroesse: "",
    beschreibung: ""
  });
  const [preisResult, setPreisResult] = useState(null);
  
  // Margen-Analyse State
  const [margenResult, setMargenResult] = useState(null);
  
  // Prognose State
  const [prognoseResult, setPrognoseResult] = useState(null);
  const [prognoseStandort, setPrognoseStandort] = useState("limburg");
  
  // Bestellung State
  const [bestellResult, setBestellResult] = useState(null);
  
  // Lieferanten State
  const [lieferantenResult, setLieferantenResult] = useState(null);
  
  // Export State
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // KI Chat - KOSTENLOS (lokale regelbasierte KI)
  const sendChat = async () => {
    if (!chatInput.trim()) return;
    
    const frage = chatInput;
    setChatInput("");
    setChatHistory(prev => [...prev, { role: "user", text: frage }]);
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/ki-lokal/chat`, { message: frage }, { withCredentials: true });
      setChatHistory(prev => [...prev, { role: "ai", text: response.data.antwort }]);
    } catch (error) {
      toast.error("Chat-Fehler: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Preiskalkulation - KOSTENLOS (lokale KI)
  const berechnePreis = async () => {
    if (!preisForm.name || !preisForm.zutaten_kosten) {
      toast.error("Bitte Name und Zutatenkosten angeben");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ki-lokal/preiskalkulation`, {
        ...preisForm,
        zutaten_kosten: parseFloat(preisForm.zutaten_kosten.replace(",", "."))
      }, { withCredentials: true });
      setPreisResult(response.data);
      toast.success("Preisempfehlung berechnet!");
    } catch (error) {
      toast.error("Fehler: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Margen-Analyse - KOSTENLOS (lokale KI)
  const analysiereMargen = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ki-lokal/margen-analyse`, { withCredentials: true });
      setMargenResult(response.data);
      toast.success("Margen-Analyse abgeschlossen!");
    } catch (error) {
      toast.error("Fehler: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Umsatzprognose - KOSTENLOS (lokale KI)
  const ladePrognose = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ki-lokal/umsatzprognose?monate_voraus=3`, { withCredentials: true });
      setPrognoseResult(response.data);
      toast.success("Prognose erstellt!");
    } catch (error) {
      toast.error("Fehler: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Bestellvorschläge - KOSTENLOS (lokale KI)
  const ladeBestellungen = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ki-lokal/bestellvorschlaege`, { withCredentials: true });
      setBestellResult(response.data);
      toast.success("Bestellvorschläge geladen!");
    } catch (error) {
      toast.error("Fehler: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Lieferanten-Analyse  
  const analysiereLieferanten = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ki-lokal/lieferanten-analyse`, { withCredentials: true });
      setLieferantenResult(response.data);
      toast.success("Analyse fertig!");
    } catch (error) {
      toast.error("Fehler: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Export Download
  const downloadExport = async (endpoint, filename) => {
    try {
      const response = await axios.get(`${API}${endpoint}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${filename} heruntergeladen!`);
    } catch (error) {
      toast.error("Download fehlgeschlagen");
    }
  };

  const tabs = [
    { id: "chat", label: "Chat Assistent", icon: MessageCircle },
    { id: "preis", label: "Preiskalkulation", icon: Calculator },
    { id: "margen", label: "Margen-Analyse", icon: TrendingUp },
    { id: "prognose", label: "Umsatzprognose", icon: BarChart3 },
    { id: "bestellung", label: "Bestellvorschläge", icon: ShoppingCart },
    { id: "lieferanten", label: "Lieferanten", icon: Truck },
    { id: "export", label: "Export", icon: Download },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
          <Brain className="text-violet-400" />
          KI-Assistent
        </h1>
        <p className="text-slate-400">
          Intelligente Analysen und Empfehlungen für Ihren Gastronomiebetrieb
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 p-1 bg-slate-800/50 rounded-lg">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? "default" : "ghost"}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 ${activeTab === tab.id ? "bg-violet-600" : "text-slate-400"}`}
            size="sm"
          >
            <tab.icon size={16} />
            <span className="hidden sm:inline">{tab.label}</span>
          </Button>
        ))}
      </div>

      {/* Chat Tab */}
      {activeTab === "chat" && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Sparkles className="text-violet-400" /> KI-Chat
            </CardTitle>
            <CardDescription>Fragen Sie alles zu Kalkulation, HACCP, Personal und mehr</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[400px] overflow-y-auto mb-4 space-y-4 p-4 bg-slate-900/50 rounded-lg">
              {chatHistory.length === 0 && (
                <p className="text-slate-500 text-center py-8">
                  Stellen Sie Ihre erste Frage...
                </p>
              )}
              {chatHistory.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === "user" 
                      ? "bg-violet-600 text-white" 
                      : "bg-slate-700 text-slate-200"
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-700 p-3 rounded-lg">
                    <Loader2 className="animate-spin text-violet-400" size={20} />
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyPress={e => e.key === "Enter" && sendChat()}
                placeholder="z.B. Wie kalkuliere ich eine Pizza Margherita?"
                className="bg-slate-700 border-slate-600"
              />
              <Button onClick={sendChat} disabled={loading} className="bg-violet-600">
                Senden
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preiskalkulation Tab */}
      {activeTab === "preis" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Preiskalkulation</CardTitle>
              <CardDescription>KI berechnet den optimalen Verkaufspreis</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="Produktname"
                value={preisForm.name}
                onChange={e => setPreisForm({...preisForm, name: e.target.value})}
                className="bg-slate-700 border-slate-600"
              />
              <Select value={preisForm.kategorie} onValueChange={v => setPreisForm({...preisForm, kategorie: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Speise">Speise</SelectItem>
                  <SelectItem value="Getränk">Getränk</SelectItem>
                  <SelectItem value="Dessert">Dessert</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Zutatenkosten (€)"
                value={preisForm.zutaten_kosten}
                onChange={e => setPreisForm({...preisForm, zutaten_kosten: e.target.value})}
                className="bg-slate-700 border-slate-600"
              />
              <Input
                placeholder="Portionsgröße (optional)"
                value={preisForm.portionsgroesse}
                onChange={e => setPreisForm({...preisForm, portionsgroesse: e.target.value})}
                className="bg-slate-700 border-slate-600"
              />
              <Button onClick={berechnePreis} disabled={loading} className="w-full bg-violet-600">
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Calculator className="mr-2" size={16} />}
                Preis berechnen
              </Button>
            </CardContent>
          </Card>

          {preisResult && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Euro className="text-green-400" />
                  Empfehlung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-green-400 mb-4">
                  {preisResult.empfohlener_preis?.toFixed(2).replace(".", ",")} €
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-3 bg-slate-700 rounded">
                    <p className="text-slate-400 text-sm">Wareneinsatz</p>
                    <p className="text-white font-semibold">{preisResult.wareneinsatz_prozent}%</p>
                  </div>
                  <div className="p-3 bg-slate-700 rounded">
                    <p className="text-slate-400 text-sm">Marge</p>
                    <p className="text-green-400 font-semibold">{preisResult.marge_euro?.toFixed(2)} €</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-4">{preisResult.begruendung}</p>
                {preisResult.alternative_preise && (
                  <div className="space-y-2">
                    <p className="text-slate-400 text-sm font-medium">Alternativen:</p>
                    {preisResult.alternative_preise.map((alt, i) => (
                      <div key={i} className="flex justify-between p-2 bg-slate-700/50 rounded">
                        <span className="text-slate-300">{alt.strategie}</span>
                        <span className="text-white font-semibold">{alt.preis?.toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Margen-Analyse Tab */}
      {activeTab === "margen" && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white">Margen-Analyse</CardTitle>
              <CardDescription>Optimierungspotenzial bei Ihren Produkten</CardDescription>
            </div>
            <Button onClick={analysiereMargen} disabled={loading} className="bg-violet-600">
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <TrendingUp className="mr-2" size={16} />}
              Analysieren
            </Button>
          </CardHeader>
          {margenResult && (
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-slate-700 rounded-lg">
                  <p className="text-slate-400">Produkte gesamt</p>
                  <p className="text-2xl font-bold text-white">{margenResult.gesamt_produkte}</p>
                </div>
                <div className="p-4 bg-slate-700 rounded-lg">
                  <p className="text-slate-400">Ø Marge</p>
                  <p className="text-2xl font-bold text-green-400">{margenResult.durchschnitt_marge}%</p>
                </div>
                <div className="p-4 bg-red-900/30 rounded-lg border border-red-700/50">
                  <p className="text-slate-400">Niedrige Marge</p>
                  <p className="text-2xl font-bold text-red-400">{margenResult.niedrige_marge_count}</p>
                </div>
              </div>

              {margenResult.niedrige_marge?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="text-red-400" size={18} />
                    Produkte mit niedriger Marge
                  </h3>
                  <div className="space-y-2">
                    {margenResult.niedrige_marge.slice(0, 5).map((p, i) => (
                      <div key={i} className="flex justify-between items-center p-3 bg-red-900/20 rounded border border-red-700/30">
                        <div>
                          <span className="text-white">{p.name}</span>
                          <span className="text-slate-400 text-sm ml-2">({p.typ})</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-slate-400">{p.verkaufspreis?.toFixed(2)} €</span>
                          <span className="text-red-400 font-semibold">{p.marge_prozent}%</span>
                          <ArrowDownRight className="text-red-400" size={16} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {margenResult.hohe_marge?.length > 0 && (
                <div>
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle className="text-green-400" size={18} />
                    Top-Performer (hohe Marge)
                  </h3>
                  <div className="space-y-2">
                    {margenResult.hohe_marge.slice(0, 5).map((p, i) => (
                      <div key={i} className="flex justify-between items-center p-3 bg-green-900/20 rounded border border-green-700/30">
                        <div>
                          <span className="text-white">{p.name}</span>
                          <span className="text-slate-400 text-sm ml-2">({p.typ})</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-slate-400">{p.verkaufspreis?.toFixed(2)} €</span>
                          <span className="text-green-400 font-semibold">{p.marge_prozent}%</span>
                          <ArrowUpRight className="text-green-400" size={16} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {margenResult.ki_analyse?.zusammenfassung && (
                <div className="mt-6 p-4 bg-violet-900/20 rounded-lg border border-violet-700/50">
                  <h3 className="text-violet-300 font-semibold mb-2 flex items-center gap-2">
                    <Sparkles size={18} /> KI-Empfehlung
                  </h3>
                  <p className="text-slate-300">{margenResult.ki_analyse.zusammenfassung}</p>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Prognose Tab */}
      {activeTab === "prognose" && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-white">Umsatzprognose</CardTitle>
              <CardDescription>KI-basierte Vorhersage mit Wetter-Einfluss</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={prognoseStandort} onValueChange={setPrognoseStandort}>
                <SelectTrigger className="w-[140px] bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="limburg">Limburg</SelectItem>
                  <SelectItem value="frankfurt">Frankfurt</SelectItem>
                  <SelectItem value="berlin">Berlin</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={ladePrognose} disabled={loading} className="bg-violet-600">
                {loading ? <Loader2 className="animate-spin" size={16} /> : "Prognose laden"}
              </Button>
            </div>
          </CardHeader>
          {prognoseResult && (
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-slate-700 rounded-lg">
                  <p className="text-slate-400">Ø Tagesumsatz</p>
                  <p className="text-2xl font-bold text-white">
                    {prognoseResult.historische_daten?.durchschnitt?.toFixed(0)} €
                  </p>
                </div>
                <div className="p-4 bg-slate-700 rounded-lg">
                  <p className="text-slate-400">Erfasste Tage</p>
                  <p className="text-2xl font-bold text-white">{prognoseResult.historische_daten?.tage_erfasst}</p>
                </div>
                <div className="p-4 bg-violet-900/30 rounded-lg border border-violet-700/50">
                  <p className="text-slate-400">Prognose heute</p>
                  <p className="text-2xl font-bold text-violet-400">
                    {prognoseResult.ki_prognose?.prognose_heute?.toFixed(0) || "—"} €
                  </p>
                </div>
              </div>

              {prognoseResult.ki_prognose?.empfehlungen && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-900/20 rounded-lg border border-blue-700/30">
                    <h4 className="text-blue-300 font-semibold mb-2">👥 Personal</h4>
                    <p className="text-slate-300 text-sm">{prognoseResult.ki_prognose.empfehlungen.personal}</p>
                  </div>
                  <div className="p-4 bg-green-900/20 rounded-lg border border-green-700/30">
                    <h4 className="text-green-300 font-semibold mb-2">🛒 Einkauf</h4>
                    <p className="text-slate-300 text-sm">{prognoseResult.ki_prognose.empfehlungen.einkauf}</p>
                  </div>
                  <div className="p-4 bg-orange-900/20 rounded-lg border border-orange-700/30">
                    <h4 className="text-orange-300 font-semibold mb-2">📣 Marketing</h4>
                    <p className="text-slate-300 text-sm">{prognoseResult.ki_prognose.empfehlungen.marketing}</p>
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Bestellvorschläge Tab */}
      {activeTab === "bestellung" && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white">Automatische Bestellvorschläge</CardTitle>
              <CardDescription>Basierend auf Lagerbestand und Verbrauch</CardDescription>
            </div>
            <Button onClick={ladeBestellungen} disabled={loading} className="bg-violet-600">
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Package className="mr-2" size={16} />}
              Vorschläge laden
            </Button>
          </CardHeader>
          {bestellResult && (
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-red-900/30 rounded-lg border border-red-700/50">
                  <p className="text-slate-400">Kritische Artikel</p>
                  <p className="text-2xl font-bold text-red-400">{bestellResult.kritische_artikel}</p>
                </div>
                <div className="p-4 bg-slate-700 rounded-lg">
                  <p className="text-slate-400">Gesamt im Lager</p>
                  <p className="text-2xl font-bold text-white">{bestellResult.gesamt_artikel}</p>
                </div>
              </div>

              {bestellResult.ki_vorschlaege?.dringend?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-red-400 font-semibold mb-2">🚨 Dringend bestellen</h3>
                  <div className="space-y-2">
                    {bestellResult.ki_vorschlaege.dringend.map((item, i) => (
                      <div key={i} className="flex justify-between p-3 bg-red-900/20 rounded border border-red-700/30">
                        <span className="text-white">{item.zutat}</span>
                        <span className="text-red-300">{item.menge} {item.einheit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bestellResult.ki_vorschlaege?.bald?.length > 0 && (
                <div>
                  <h3 className="text-yellow-400 font-semibold mb-2">⏰ Bald bestellen</h3>
                  <div className="space-y-2">
                    {bestellResult.ki_vorschlaege.bald.map((item, i) => (
                      <div key={i} className="flex justify-between p-3 bg-yellow-900/20 rounded border border-yellow-700/30">
                        <span className="text-white">{item.zutat}</span>
                        <span className="text-yellow-300">{item.menge} {item.einheit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Lieferanten Tab */}
      {activeTab === "lieferanten" && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white">Lieferanten-Analyse</CardTitle>
              <CardDescription>Einsparpotenzial und Optimierung</CardDescription>
            </div>
            <Button onClick={analysiereLieferanten} disabled={loading} className="bg-violet-600">
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Truck className="mr-2" size={16} />}
              Analysieren
            </Button>
          </CardHeader>
          {lieferantenResult && (
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-slate-700 rounded-lg">
                  <p className="text-slate-400">Lieferanten</p>
                  <p className="text-2xl font-bold text-white">{lieferantenResult.lieferanten_count}</p>
                </div>
                <div className="p-4 bg-slate-700 rounded-lg">
                  <p className="text-slate-400">Einkaufsvolumen</p>
                  <p className="text-2xl font-bold text-green-400">
                    {lieferantenResult.gesamt_einkaufsvolumen?.toFixed(2)} €
                  </p>
                </div>
              </div>

              {lieferantenResult.ki_analyse?.einsparpotenzial && (
                <div className="p-4 bg-green-900/20 rounded-lg border border-green-700/50 mb-4">
                  <h3 className="text-green-300 font-semibold mb-2">💰 Einsparpotenzial</h3>
                  <p className="text-slate-300">{lieferantenResult.ki_analyse.einsparpotenzial}</p>
                </div>
              )}

              {lieferantenResult.ki_analyse?.empfehlungen && (
                <div>
                  <h3 className="text-white font-semibold mb-2">Empfehlungen</h3>
                  <ul className="space-y-2">
                    {lieferantenResult.ki_analyse.empfehlungen.map((emp, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-300">
                        <ChevronRight className="text-violet-400 mt-1 flex-shrink-0" size={16} />
                        {emp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Export Tab */}
      {activeTab === "export" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="text-red-400" /> HACCP-Protokoll
              </CardTitle>
              <CardDescription>Dokumentation für das Gesundheitsamt</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button onClick={() => downloadExport("/export/haccp-protokoll?format=pdf", "HACCP_Protokoll.pdf")} className="w-full bg-red-600">
                <Download size={16} className="mr-2" /> PDF herunterladen
              </Button>
              <Button onClick={() => downloadExport("/export/haccp-protokoll?format=xlsx", "HACCP_Protokoll.xlsx")} variant="outline" className="w-full border-slate-600">
                <Download size={16} className="mr-2" /> Excel herunterladen
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calculator className="text-green-400" /> Kalkulation
              </CardTitle>
              <CardDescription>Alle Speisen & Getränke mit Margen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button onClick={() => downloadExport("/export/kalkulation?format=pdf", "Kalkulation.pdf")} className="w-full bg-green-600">
                <Download size={16} className="mr-2" /> PDF herunterladen
              </Button>
              <Button onClick={() => downloadExport("/export/kalkulation?format=xlsx", "Kalkulation.xlsx")} variant="outline" className="w-full border-slate-600">
                <Download size={16} className="mr-2" /> Excel herunterladen
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="text-blue-400" /> Umsatzbericht
              </CardTitle>
              <CardDescription>Einnahmen der letzten 30 Tage</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button onClick={() => downloadExport("/export/umsatzbericht?format=pdf", "Umsatzbericht.pdf")} className="w-full bg-blue-600">
                <Download size={16} className="mr-2" /> PDF herunterladen
              </Button>
              <Button onClick={() => downloadExport("/export/umsatzbericht?format=xlsx", "Umsatzbericht.xlsx")} variant="outline" className="w-full border-slate-600">
                <Download size={16} className="mr-2" /> Excel herunterladen
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Utensils className="text-violet-400" /> Speisekarte
              </CardTitle>
              <CardDescription>Professionelle Menükarte mit Allergenen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button onClick={() => downloadExport("/speisekarte/generieren?stil=elegant", "Speisekarte.pdf")} className="w-full bg-violet-600">
                <Download size={16} className="mr-2" /> Elegant
              </Button>
              <Button onClick={() => downloadExport("/speisekarte/generieren?stil=modern", "Speisekarte_Modern.pdf")} variant="outline" className="w-full border-slate-600">
                <Download size={16} className="mr-2" /> Modern
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default KIAssistent;
